import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button } from "react-bootstrap";
import i18n from "i18next";
import { ID_PREFIX_FILE } from "../../../../../../common/constants.es6";
import { getToken } from "../../../../../../common/environment_config";

export default function SoundTextToSpeechComponent(props) {
  const { onElementSelected, onServerModal, ttsGenerateSoundFile, icon, name } = props;

  const [processing, setProcessing] = useState(false);
  const [fileUrl, setFileUrl] = useState("");
  const [soundLoaded, setSoundLoaded] = useState(false);
  const [estimatedDuration, setEstimatedDuration] = useState({ min: 0, max: 0 });
  const fetchRequestRef = useRef(0);

  // Provider bazlı konuşma hızları (karakter/saniye). Min = yavaş okuma, Max = hızlı okuma.
  // Değerler ampirik ölçümlere göre kalibre edilmiştir; TR/EN için makul aralıklar üretir.
  const PROVIDER_SPEECH_RATES = {
    // Azure Neural TTS — oldukça tutarlı, orta-yüksek tempoda okur
    azure: { minCps: 13.5, maxCps: 16.5, pausePerSentence: 0.35 },
    // ElevenLabs — daha doğal duraklamalar, biraz daha yavaş
    elevenlabs: { minCps: 12.0, maxCps: 15.0, pausePerSentence: 0.5 },
    // Gemini TTS — dengeli, duraklamalar biraz daha belirgin
    gemini: { minCps: 12.5, maxCps: 15.5, pausePerSentence: 0.45 },
  };

  const estimateSpeechDuration = (text, providerKey) => {
    if (!text || !text.trim()) return { min: 0, max: 0 };
    const conf = PROVIDER_SPEECH_RATES[providerKey] || PROVIDER_SPEECH_RATES.azure;
    const cleaned = text.trim();
    const charCount = cleaned.length;
    const sentenceCount = (cleaned.match(/[.!?…]+/g) || []).length || 1;
    const pauseTotal = sentenceCount * conf.pausePerSentence;
    // Hızlı okuma (maxCps) -> minimum süre, yavaş okuma (minCps) -> maksimum süre
    const minSec = charCount / conf.maxCps + pauseTotal * 0.6;
    const maxSec = charCount / conf.minCps + pauseTotal;
    return { min: Math.max(1, Math.round(minSec)), max: Math.max(1, Math.round(maxSec)) };
  };

  const getProviderKey = () => {
    const aiNameEl = document.getElementById("tts_ainame");
    const providerMap = { 0: "azure", 1: "elevenlabs", 2: "gemini", 4: "sestek" };
    return providerMap[aiNameEl ? aiNameEl.value : 0] || "azure";
  };

  const recalcDuration = () => {
    const promptEl = document.getElementById("tts_prompttext");
    const text = promptEl ? promptEl.value : "";
    setEstimatedDuration(estimateSpeechDuration(text, getProviderKey()));
  };

  const formatDuration = (sec) => {
    if (!sec) return "0 sn";
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return m > 0 ? `${m} dk ${s} sn` : `${s} sn`;
  };

  // Initialize AI speech config on mount
  useEffect(() => {
    if (window.ai_speech_conf && window.ai_speech_conf.length > 0) {
      tts_aiNameChanged();

      if (
        window.aiSelectedVoiceServiceType > -1 &&
        window.aiSelectedVoiceName &&
        window.aiSelectedVoiceName.length > 0
      ) {
        const aisType = window.ai_speech_conf.find(
          (a) => a.value == window.aiSelectedVoiceServiceType
        );
        if (aisType != null) {
          for (let index = 0; index < aisType.lang.length; index++) {
            const aiLang = aisType.lang[index];

            if (aiLang.voices.filter((a) => a.value == window.aiSelectedVoiceName).length > 0) {
              const aiNameEl = document.getElementById("tts_ainame");
              if (aiNameEl) {
                aiNameEl.value = window.aiSelectedVoiceServiceType;
              }
              window.aiSelectedVoiceLang = aiLang.name;

              tts_aiNameChanged();
              const langEl = document.getElementById("tts_lang");
              if (langEl) {
                langEl.value = window.aiSelectedVoiceLang;
              }
              const savedVoiceName = window.aiSelectedVoiceName;
              tts_aiLangChanged().then(() => {
                const voiceEl = document.getElementById("tts_voice");
                if (voiceEl) voiceEl.value = savedVoiceName;
              });
              break;
            }
          }
        }
      } else if (window.idealStudioVoiceProfile && window.idealStudioVoiceProfile.voiceName) {
        const profile = window.idealStudioVoiceProfile;
        const providerConf = window.ai_speech_conf.find((a) => a.value == profile.voiceServiceType);
        if (providerConf) {
          const aiNameEl = document.getElementById("tts_ainame");
          if (aiNameEl) aiNameEl.value = profile.voiceServiceType;
          tts_aiNameChanged();

          const profileLang = profile.voiceLanguage || "";
          const matchedLang = providerConf.lang.find(
            (l) => l.name === profileLang || l.name.startsWith(profileLang.split("-")[0])
          );
          if (matchedLang) {
            const langEl = document.getElementById("tts_lang");
            if (langEl) langEl.value = matchedLang.name;
            const savedVoiceName = profile.voiceName;
            tts_aiLangChanged().then(() => {
              const voiceEl = document.getElementById("tts_voice");
              if (voiceEl) voiceEl.value = savedVoiceName;
            });
          }
        }
      }
    } else {
      onServerModal(
        i18n.t("error.warning"),
        "<span>" + i18n.t("CheckTheSubscriptionInformation") + "</span>",
        false
      );
    }

    return () => {
      const audio = document.getElementById("tts_audio");
      if (audio) {
        audio.pause();
      }
    };
  }, [onServerModal]);

  const tts_aiNameChanged = () => {
    const langEl = document.getElementById("tts_lang");
    if (langEl) {
      langEl.innerHTML = "";

      const selectedNameEl = document.getElementById("tts_ainame");
      if (!selectedNameEl) {
        return;
      }
      const _selectedName = selectedNameEl.value;
      const aiConfig = window.ai_speech_conf.find((a) => a.value == _selectedName);
      if (!aiConfig) {
        return;
      }
      const _langArr = aiConfig.lang;

      let opt;
      for (let i = 0; i < _langArr.length; i++) {
        opt = document.createElement("option");
        opt.value = _langArr[i].name;
        opt.innerHTML = _langArr[i].name;
        langEl.add(opt);
      }
      tts_aiLangChanged();
      const langRow = document.getElementById("tts_lang_row");
      if (langRow) {
        if (_langArr.length < 2) {
          langRow.classList.add("hidden");
        } else {
          langRow.classList.remove("hidden");
        }
      }
    }
  };

  const tts_aiLangChanged = async () => {
    const langEl = document.getElementById("tts_lang");
    const voiceEl = document.getElementById("tts_voice");
    const aiNameEl = document.getElementById("tts_ainame");
    if (!langEl || !voiceEl || !aiNameEl) return;

    const _selectedName = aiNameEl.value;
    const _selectedLang = langEl.value;

    const providerMap = { 0: "azure", 1: "elevenlabs", 2: "gemini", 4: "sestek" };
    const provider = providerMap[_selectedName] || "azure";

    voiceEl.innerHTML = "";
    const loadingOpt = document.createElement("option");
    loadingOpt.innerHTML = "...";
    voiceEl.add(loadingOpt);

    const reqId = ++fetchRequestRef.current;
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.MW_API_URL}api/ideal-studio/tts-voices/${provider}?language=${_selectedLang}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (reqId !== fetchRequestRef.current) return;
      voiceEl.innerHTML = "";
      if (response.ok) {
        const voices = await response.json();
        if (reqId !== fetchRequestRef.current) return;
        voices.forEach((voice) => {
          const opt = document.createElement("option");
          opt.value = voice.id || voice.voice_id || voice.ShortName || voice.value || voice.name;
          opt.innerHTML = voice.name || voice.DisplayName || voice.displayName || opt.value;
          voiceEl.add(opt);
        });
      }
    } catch (e) {
      if (reqId === fetchRequestRef.current) voiceEl.innerHTML = "";
    }
  };

  const ttsGenerateSoundHandler = (e) => {
    e.stopPropagation();

    const promptEl = document.getElementById("tts_prompttext");
    const aiNameEl = document.getElementById("tts_ainame");
    const langEl = document.getElementById("tts_lang");
    const voiceEl = document.getElementById("tts_voice");

    if (!promptEl || !aiNameEl || !langEl || !voiceEl) {
      return;
    }

    const _prompt = promptEl.value;
    // En son kullanılan AI anlatım metnini global değişkende sakla
    try {
      if (_prompt && _prompt.trim()) {
        window.lastAiNarrationPrompt = _prompt;
      }
    } catch (err) {}
    const _selectedName = aiNameEl.value;
    const _selectedLang = langEl.value;
    const _selectedVoice = voiceEl.value;

    if (_prompt.replaceAll(" ", "") == "") {
      onServerModal(
        i18n.t("error.warning"),
        "<span>" + i18n.t("YouMustEnterAVoiceoverText") + "</span>",
        false
      );
      return;
    }

    const idd = ID_PREFIX_FILE + Date.now();
    const fnamee = "AI-" + _prompt;

    setProcessing(true);
    setFileUrl("");
    setSoundLoaded(false);

    ttsGenerateSoundFile(
      {
        prompt: _prompt,
        aiType: _selectedName,
        langName: _selectedLang,
        voiceName: _selectedVoice,
        id: idd,
        fileName: fnamee,
      },
      (isSuccess, errorStr, url) => {
        if (isSuccess && url) {
          setProcessing(false);
          setFileUrl(url);
          setSoundLoaded(true);
          onElementSelected(fnamee, url, "audio", idd);
        } else {
          onServerModal(
            i18n.t("FileModal.APIProviders.error"),
            "<span>" + errorStr + "</span>",
            false
          );
          setProcessing(false);
          setFileUrl("");
          setSoundLoaded(false);
        }
      }
    );
  };

  return (
    <div
      className="contentComponent"
      style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}
    >
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          padding: "8px 8px 12px 0",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
            gap: 8,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {icon ? <img className="fileMenuIcon" src={icon} alt="" /> : <span>{name}</span>}
            <span style={{ fontSize: 13, color: "#6b7280" }}>
              {i18n.t("ToMakeVoiceoveWithAIYouCanproceedUsingTheFollowingOptions")}
            </span>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontWeight: 500, fontSize: 14 }}>{i18n.t("AiPromptText")}</span>
            <textarea
              id="tts_prompttext"
              maxLength={4096}
              rows={4}
              className="form-control"
              style={{ width: "100%" }}
              placeholder={i18n.t("TypeTheTextYouWantToVoiceover")}
              onChange={recalcDuration}
              onInput={recalcDuration}
            ></textarea>
            {estimatedDuration.max > 0 && (
              <span style={{ fontSize: 12, color: "#6b7280" }}>
                Tahmini ses süresi: ~{formatDuration(estimatedDuration.min)} –{" "}
                {formatDuration(estimatedDuration.max)}
              </span>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontWeight: 500, fontSize: 14 }}>{i18n.t("AiService")}</span>
            <select
              id="tts_ainame"
              className="form-control"
              onChange={() => {
                tts_aiNameChanged();
                recalcDuration();
              }}
              style={{ width: "100%" }}
            >
              {/* "Sestek" (value 4) sağlayıcı seçeneği gizlendi — ai_config'ten
                  silinmedi, render'da filtrelendi. Geri açmak için filtreyi kaldır. */}
              {window.ai_speech_conf &&
                window.ai_speech_conf
                  .filter((a) => Number(a.value) !== 4)
                  .map((a) => (
                    <option key={a.name} value={a.value}>
                      {a.name}
                    </option>
                  ))}
            </select>
          </div>

          <div id="tts_lang_row" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontWeight: 500, fontSize: 14 }}>{i18n.t("AiLanguage")}</span>
            <select
              id="tts_lang"
              className="form-control"
              onChange={tts_aiLangChanged}
              style={{ width: "100%" }}
            >
              <option>_</option>
            </select>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontWeight: 500, fontSize: 14 }}>{i18n.t("AiSound")}</span>
            <select id="tts_voice" className="form-control" style={{ width: "100%" }}>
              <option>_</option>
            </select>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: 4,
            }}
          >
            <Button
              id="tts_createfile"
              className="btn-primary"
              disabled={!window.ai_speech_conf || !window.ai_speech_conf.length || processing}
              onClick={ttsGenerateSoundHandler}
            >
              {i18n.t("Create")}
            </Button>
            <span
              id="tts_usage"
              style={{
                fontSize: 13,
                color: "#6b7280",
                visibility: processing ? "visible" : "hidden",
              }}
            >
              {i18n.t("PleaseWait") || "Lütfen Bekleyin..."}
            </span>
          </div>

          {soundLoaded && fileUrl && (
            <div
              id="tts_audio_file_row"
              style={{ marginTop: 10, marginBottom: 10, textAlign: "center" }}
            >
              <audio id="tts_audio" controls src={fileUrl} />
              <h6>
                {i18n.t("TTS.AddedToMyFiles") || "Oluşturduğunuz ses 'Dosyalarım' a eklenmiştir."}
                <br />
                {i18n.t("TTS.AccessPreviousSounds") ||
                  "Daha önce oluşturduğunuz seslere buradan ulaşabilir ya da kullanmadığınız sesleri silebilirsiniz."}
              </h6>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

SoundTextToSpeechComponent.propTypes = {
  /**
   * Selected Element
   */
  elementSelected: PropTypes.any,
  /**
   * Select element callback
   */
  onElementSelected: PropTypes.func.isRequired,
  /**
   * Icon that identifies the API provider
   */
  icon: PropTypes.any,
  /**
   * API Provider name
   */
  name: PropTypes.string,
};
